import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Input, FormGroup, Label, Form, Row, Col, Card, CardBody, InputGroup, InputGroupText } from "reactstrap";
import Select from "react-select";
import { useDispatch } from "react-redux";
import { loadingTrue, loadingFalse } from "../../Reducer/loaderSlice";
import Handler from "../../Helpers/Handler";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import { FiUser, FiDollarSign, FiFileText, FiPlusCircle, FiArrowLeft } from "react-icons/fi";

const StaffDeposit = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    // States
    const [staffOptions, setStaffOptions] = useState([]);
    const [formData, setFormData] = useState({
        staff_id: "",
        amount: "",
        note: ""
    });

    const [balanceSummary, setBalanceSummary] = useState(null);
    const [errors, setErrors] = useState({});

    // 1. Load Staffs
    useEffect(() => {
        const getStaffs = async () => {
            // Start-laye loading true panrathu nallathu machan
            dispatch(loadingTrue());
            try {
                const res = await Handler({ method: "GET", url: "/staff/list.php" });

                if (res?.success) {
                    // 🔥 Machan, staff.staff.name structure-ku mathiyachu
                    const formattedOptions = res.data.map(staff => ({
                        value: staff.id,
                        // staff object kulla staff name irundha edukum, illana fallback check
                        label: staff.staff?.name?.toUpperCase() || staff.name?.toUpperCase() || "UNKNOWN STAFF"
                    }));

                    setStaffOptions(formattedOptions);
                } else {
                    console.error("Staff fetch failed:", res?.message);
                }
            } catch (err) {
                toast.error("Error loading staff list");
                console.error("Staff API error:", err);
            } finally {
                // Error vanthaalum, success aanaalum loading stop aaganum
                dispatch(loadingFalse());
            }
        };
        getStaffs();
    }, [dispatch]); // dispatch-ah dependency-la sethukko machan, best practice

    // 2. Load Balance
    useEffect(() => {
        if (formData.staff_id) {
            const fetchBalance = async () => {
                try {
                    const res = await Handler({
                        method: "GET",
                        url: `/deposit/balance.php?staff_id=${formData.staff_id}`
                    });
                    if (res?.success) { setBalanceSummary(res.data.summary); dispatch(loadingFalse()) }
                    else { dispatch(loadingFalse()); }
                } catch (err) { console.error("Balance fetch error"); }
            };
            fetchBalance();
        } else {
            setBalanceSummary(null);
        }
    }, [formData.staff_id]);

    // 🔥 Separate Validation Logic Machan
    const validateForm = () => {
        let formErrors = {};
        let isValid = true;

        if (!formData.staff_id) {
            formErrors.staff_id = "Please select a staff member";
            isValid = false;
        }

        if (!formData.amount || parseFloat(formData.amount) <= 0) {
            formErrors.amount = "Enter a valid deposit amount";
            isValid = false;
        }

        setErrors(formErrors);
        if (!isValid) toast.error("Please fill required fields correctly!");
        return isValid;
    };

    const handleSelectChange = (selectedOption) => {
        setFormData({ ...formData, staff_id: selectedOption ? selectedOption.value : "" });
        if (errors.staff_id) setErrors({ ...errors, staff_id: "" });
    };

    const handleChange = (e) => {
        const { name, value, type } = e.target;

        // 🔥 Number field-la minus block panna logic machan
        if (type === "number") {
            setFormData({ ...formData, [name]: value === "" ? "" : Math.abs(parseFloat(value)) });
        } else {
            setFormData({ ...formData, [name]: value });
        }

        if (errors[name]) setErrors({ ...errors, [name]: "" });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) return; // Separate validation check trigger

        dispatch(loadingTrue());
        try {
            const res = await Handler({ method: "POST", url: "/deposit/add.php", data: formData });
            if (res?.success) {
                Swal.fire("Success!", `₹${formData.amount} deposited successfully`, "success");
                setFormData({ staff_id: "", amount: "", note: "" }); // Reset
                setBalanceSummary(null);
                navigate('/deposit')
                dispatch(loadingFalse());
            } else {
                if (res?.errors) setErrors(res.errors);
                else toast.error(res?.message || "Failed to add deposit");
                dispatch(loadingFalse());
            }
        } catch (err) {
            Swal.fire("Error", "Server error occurred", "error");
        } finally {
            dispatch(loadingFalse());
        }
    };

    const customSelectStyles = {
        control: (provided, state) => ({
            ...provided,
            minHeight: '50px',
            borderRadius: '10px',
            borderColor: state.isFocused ? '#f16436' : (errors.staff_id ? '#dc3545' : '#dee2e6'),
            boxShadow: 'none',
            backgroundColor: '#f8f9fa'
        }),
        menuPortal: base => ({ ...base, zIndex: 9999 })
    };

    return (
        <div className="page-container p-3 bg-light min-vh-100">
            <div className="page-header mb-4 d-md-flex justify-content-between align-items-center">
                <div>
                    <h3 className="fw-bold text-dark m-0">Add Staff Deposit</h3>
                    <p className="text-muted small">Allocate funds to staff for procurement and operational needs.</p>
                </div>
                <Button color="white" className="shadow-sm border rounded-pill px-4 d-flex align-items-center gap-2" onClick={() => navigate(-1)}>
                    <FiArrowLeft /> Back
                </Button>
            </div>

            <Row className="g-4">
                <Col lg={8}>
                    <Card className="border-0 shadow-sm rounded-4 overflow-hidden">
                        <div className="bg-primary p-1"></div>
                        <CardBody className="p-4 p-md-5">
                            <Form onSubmit={handleSubmit}>
                                <Row className="g-4">
                                    <Col md={6}>
                                        <FormGroup>
                                            <Label className="small fw-bold text-uppercase text-muted"><FiUser className="me-1" /> Staff Member *</Label>
                                            <Select
                                                options={staffOptions}
                                                value={staffOptions.find(opt => opt.value === formData.staff_id)}
                                                onChange={handleSelectChange}
                                                placeholder="Choose staff..."
                                                isClearable
                                                styles={customSelectStyles}
                                                menuPortalTarget={document.body}
                                            />
                                            {errors.staff_id && <small className="text-danger mt-1 d-block">{errors.staff_id}</small>}
                                        </FormGroup>
                                    </Col>

                                    <Col md={6}>
                                        <FormGroup>
                                            <Label className="small fw-bold text-uppercase text-muted"><FiDollarSign className="me-1" /> Deposit Amount (₹) *</Label>
                                            <InputGroup className="border rounded-3 overflow-hidden shadow-none bg-light" style={{ height: '50px' }}>
                                                <InputGroupText className="bg-transparent border-0 text-muted">₹</InputGroupText>
                                                <Input
                                                    type="number"
                                                    name="amount"
                                                    placeholder="0.00"
                                                    className={`bg-transparent border-0 fw-bold ${errors.amount ? "is-invalid" : ""}`}
                                                    value={formData.amount}
                                                    onChange={handleChange}
                                                    onKeyDown={(e) => ["-", "e", "E"].includes(e.key) && e.preventDefault()}
                                                />
                                            </InputGroup>
                                            {errors.amount && <small className="text-danger mt-1 d-block">{errors.amount}</small>}
                                        </FormGroup>
                                    </Col>

                                    <Col md={12}>
                                        <FormGroup>
                                            <Label className="small fw-bold text-uppercase text-muted"><FiFileText className="me-1" /> Description / Purpose</Label>
                                            <Input
                                                type="textarea"
                                                name="note"
                                                placeholder="Add details about this deposit..."
                                                rows="3"
                                                className="bg-light border-0 rounded-3"
                                                value={formData.note}
                                                onChange={handleChange}
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>

                                <div className="mt-5 pt-3 border-top d-flex justify-content-end">
                                    <Button type="submit" color="primary" className="add-btn px-5 py-2 rounded-3 shadow-sm fw-bold d-flex align-items-center gap-2">
                                        <FiPlusCircle /> Confirm & Add Deposit
                                    </Button>
                                </div>
                            </Form>
                        </CardBody>
                    </Card>
                </Col>

                <Col lg={4}>
                    <Card className="border-0 shadow-sm bg-primary text-white rounded-4 overflow-hidden sticky-top" style={{ top: '100px' }}>
                        <CardBody className="p-4">
                            <div className="text-center">
                                <p className="opacity-75 small text-uppercase fw-bold ls-1 mb-1">Available Balance</p>
                                <h2 className="fw-bold mb-4">
                                    ₹ {balanceSummary ? balanceSummary.balance?.toLocaleString() : "0.00"}
                                </h2>
                            </div>
                            <div className="bg-white bg-opacity-10 p-3 rounded-3">
                                <div className="d-flex justify-content-between small mb-2">
                                    <span className="opacity-75">Life-time Deposits:</span>
                                    <span className="fw-bold">₹ {balanceSummary ? balanceSummary.total_deposit?.toLocaleString() : "0"}</span>
                                </div>
                                <div className="d-flex justify-content-between small">
                                    <span className="opacity-75">Settled Expenses:</span>
                                    <span className="fw-bold text-warning">₹ {balanceSummary ? balanceSummary.total_expense?.toLocaleString() : "0"}</span>
                                </div>
                            </div>
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            <style>
                {`
                    .ls-1 { letter-spacing: 0.5px; }
                    .add-btn:hover { transform: translateY(-1px); transition: 0.2s; }
                `}
            </style>
        </div>
    );
};

export default StaffDeposit;